import React from 'react'
import {
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonItemDivider,
  IonLabel,
  IonPage,
  IonSegment,
  IonSegmentButton,
  IonToolbar,
  useIonViewDidEnter,
} from '@ionic/react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { calculateIsNeonatalAction, clearFluidsFields, setTransportType } from '../store/fluids.actions'
import PCRHeader from '../../../components/pcrHeader/PCRHeader.component'
import FluidsContent from '../pageContent/Fluids.content'
import { getFluidsSelector } from '../selectors'
import HeaderNav from '../../../components/headerNav/HeaderNav.component'
import { showAlert } from '../../../components/alert/store/alert.actions'

const Fluids: React.FC = () => {
  const history = useHistory()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const fluidsState = useSelector(getFluidsSelector)
  const { transportType } = fluidsState

  useIonViewDidEnter(() => {
    dispatch(calculateIsNeonatalAction())
  })

  const onBackClick = () => {
    history.push(TabConstants.CHART_ROUTE)
  }

  const onSegmentChange = (e: CustomEvent) => {
    const value = e.detail.value || 'before'
    dispatch(setTransportType(value as 'before' | 'during'))
  }

  const isClearAllDisabled = () => {
    const {
      transportType,
      isNeonatal,
      currentCustomFluidValue,
      currentCustomFluidOptionId,
      currentCustomFluidTransportPeriod,
      ...fluidFields
    } = fluidsState
    return !Object.entries(fluidFields).some(([key, value]) => {
      if (value === null || value === undefined) return false
      if (key === 'customFluids') {
        return Array.isArray(value) && value.length > 0
      }
      if (typeof value === 'object') return value !== null
      if (typeof value === 'string') return value.trim().length > 0
      return false
    })
  }

  const resetFluidsStateHandler = () => {
    dispatch(clearFluidsFields())
  }

  return (
    <IonPage id='FluidsPage'>
      <PCRHeader />
      <HeaderNav
        prevOnClick={onBackClick}
        prevLabel={t('common.buttons.Back')}
        isBackButton={true}
        nextLabel={t('common.buttons.Save')}
        title={t('fluids.Fluids')}
      />

      <IonItemDivider color='light' className='ion-padding'>
        <IonLabel className='grid-item-divider-label' color='medium'>
          {t('fullChart.patient.fluids.FluidsSubHeader')}
        </IonLabel>
      </IonItemDivider>

      <IonToolbar color='light'>
        <IonSegment color='dark' className='segment-bottom-padding' value={transportType} onIonChange={onSegmentChange}>
          <IonSegmentButton data-testid='BeforeTransportSegment' value={'before'}>
            <IonLabel>{t('fluids.BeforeTransport')}</IonLabel>
          </IonSegmentButton>
          <IonSegmentButton data-testid='DuringTransportSegment' value={'during'}>
            <IonLabel>{t('fluids.DuringTransport')}</IonLabel>
          </IonSegmentButton>
        </IonSegment>
      </IonToolbar>
      <IonContent color='light'>
        <FluidsContent transportType={transportType} />
      </IonContent>
      <IonFooter>
        <IonToolbar color='light' className='p-10'>
          <IonButtons className='btn-grps'>
            <IonButton
              className='btn btn-tablet'
              color='danger'
              fill='outline'
              expand='block'
              disabled={isClearAllDisabled()}
              onClick={() =>
                dispatch(
                  showAlert({
                    header: '',
                    message: t('fluids.ClearAllConfirmation'),
                    buttons: [
                      { text: t('common.buttons.Cancel') },
                      { text: t('common.buttons.Ok'), role: 'destructive', handler: resetFluidsStateHandler },
                    ],
                  })
                )
              }>
              {t('common.buttons.ClearAll')}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonFooter>
    </IonPage>
  )
}

export default Fluids
